import { ADMIN_ROLES, type AdminRole } from "../../contracts/admin-permissions";

type AdminDashboardEnv = {
  VITE_ADMIN_API_BASE_URL?: string;
  VITE_ADMIN_AUTH_MODE?: string;
  VITE_ADMIN_AUTH_PROVIDER?: string;
  VITE_ADMIN_ENVIRONMENT_NAME?: string;
  VITE_ADMIN_RELEASE?: string;
  VITE_ADMIN_ROLES?: string;
  VITE_ADMIN_GCP_PROJECT_ID?: string;
  VITE_ADMIN_CLOUD_RUN_SERVICE_NAME?: string;
  EXPO_PUBLIC_FIREBASE_API_KEY?: string;
  EXPO_PUBLIC_FIREBASE_APP_ID?: string;
  EXPO_PUBLIC_FIREBASE_PROJECT_ID?: string;
  EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN?: string;
  EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID?: string;
  EXPO_PUBLIC_FIREBASE_STORAGE_BUCKET?: string;
};

declare const __PETLOG_ADMIN_PACKAGE_VERSION__: string | undefined;

export type AdminDashboardAuthProvider = "injected" | "firebase-google-popup";

export type AdminDashboardFirebaseConfig = {
  apiKey: string;
  appId: string;
  projectId: string;
  authDomain: string;
  messagingSenderId: string;
  storageBucket: string;
};

export type AdminDashboardObservabilityConfig = {
  cloudProjectId?: string;
  cloudRunServiceName?: string;
};

export type AdminDashboardRuntimeConfig = {
  apiBaseUrl: string;
  authMode: "fixture" | "admin-auth";
  authProvider: AdminDashboardAuthProvider;
  bundleFingerprint: string;
  environmentName: string;
  release?: string;
  adminRoles: AdminRole[];
  firebaseConfig?: AdminDashboardFirebaseConfig;
  observability?: AdminDashboardObservabilityConfig;
};

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readEnv(): AdminDashboardEnv {
  return (import.meta as ImportMeta & { env?: AdminDashboardEnv }).env ?? {};
}

function readBuildRelease(): string {
  if (
    typeof __PETLOG_ADMIN_PACKAGE_VERSION__ === "string" &&
    __PETLOG_ADMIN_PACKAGE_VERSION__.trim().length > 0
  ) {
    return __PETLOG_ADMIN_PACKAGE_VERSION__.trim();
  }

  return "dev-local";
}

export function resolveAdminDashboardRelease(
  envRelease: string | undefined,
  buildRelease: string = readBuildRelease(),
): string {
  const normalizedEnvRelease = envRelease?.trim();
  if (normalizedEnvRelease) {
    return normalizedEnvRelease;
  }

  return buildRelease.trim() || "dev-local";
}

function resolveBundleFingerprint(moduleUrl: string | undefined): string {
  if (!moduleUrl || moduleUrl.trim().length === 0) {
    return "unknown-bundle";
  }

  const sanitized = moduleUrl.trim().split("?")[0]?.split("#")[0] ?? moduleUrl.trim();
  const fileName = sanitized.split("/").pop();
  return fileName && fileName.length > 0 ? fileName : sanitized;
}

function normalizeAuthMode(value: string | undefined): AdminDashboardRuntimeConfig["authMode"] {
  return value === "admin-auth" ? "admin-auth" : "fixture";
}

function normalizeAuthProvider(value: string | undefined): AdminDashboardAuthProvider {
  return value === "firebase-google-popup" ? "firebase-google-popup" : "injected";
}

function normalizeAdminRoles(value: string | undefined): AdminRole[] {
  if (value === undefined || value.trim().length === 0) {
    return ["support_readonly"];
  }

  const roles = value
    .split(",")
    .map((item) => item.trim())
    .filter((item): item is AdminRole => ADMIN_ROLES.includes(item as AdminRole));

  return roles.length > 0 ? roles : ["support_readonly"];
}

function readFirebaseConfig(env: AdminDashboardEnv): AdminDashboardFirebaseConfig | undefined {
  const apiKey = env.EXPO_PUBLIC_FIREBASE_API_KEY?.trim();
  const appId = env.EXPO_PUBLIC_FIREBASE_APP_ID?.trim();
  const projectId = env.EXPO_PUBLIC_FIREBASE_PROJECT_ID?.trim();
  const authDomain = env.EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN?.trim();
  const messagingSenderId = env.EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID?.trim();
  const storageBucket = env.EXPO_PUBLIC_FIREBASE_STORAGE_BUCKET?.trim();

  if (!apiKey || !appId || !projectId || !authDomain || !messagingSenderId || !storageBucket) {
    return undefined;
  }

  return {
    apiKey,
    appId,
    projectId,
    authDomain,
    messagingSenderId,
    storageBucket,
  };
}

function createRuntimeConfigFromEnv(env: AdminDashboardEnv): AdminDashboardRuntimeConfig {
  const defaultOrigin =
    typeof window !== "undefined" && typeof window.location?.origin === "string"
      ? window.location.origin
      : "http://localhost";

  return {
    apiBaseUrl: env.VITE_ADMIN_API_BASE_URL?.trim() || defaultOrigin,
    authMode: normalizeAuthMode(env.VITE_ADMIN_AUTH_MODE),
    authProvider: normalizeAuthProvider(env.VITE_ADMIN_AUTH_PROVIDER),
    bundleFingerprint: resolveBundleFingerprint(import.meta.url),
    environmentName: env.VITE_ADMIN_ENVIRONMENT_NAME?.trim() || "local-fixture",
    release: resolveAdminDashboardRelease(env.VITE_ADMIN_RELEASE),
    adminRoles: normalizeAdminRoles(env.VITE_ADMIN_ROLES),
    firebaseConfig: readFirebaseConfig(env),
    observability: {
      cloudProjectId: env.VITE_ADMIN_GCP_PROJECT_ID?.trim() || undefined,
      cloudRunServiceName: env.VITE_ADMIN_CLOUD_RUN_SERVICE_NAME?.trim() || undefined,
    },
  };
}

function normalizeStringOverride(value: unknown, fallback: string): string {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : fallback;
}

function normalizeAdminRolesOverride(value: unknown, fallback: AdminRole[]): AdminRole[] {
  if (!Array.isArray(value)) {
    return fallback;
  }

  const roles = value.filter((item): item is AdminRole => typeof item === "string" && ADMIN_ROLES.includes(item as AdminRole));
  return roles.length > 0 ? roles : fallback;
}

function normalizeRuntimeConfigOverrides(
  overrides: Partial<AdminDashboardRuntimeConfig> | undefined,
  defaults: AdminDashboardRuntimeConfig,
): Partial<AdminDashboardRuntimeConfig> {
  if (!isObject(overrides)) {
    return {};
  }

  return {
    apiBaseUrl: normalizeStringOverride(overrides.apiBaseUrl, defaults.apiBaseUrl),
    authMode: overrides.authMode === "admin-auth" ? "admin-auth" : defaults.authMode,
    authProvider: overrides.authProvider === "firebase-google-popup" ? "firebase-google-popup" : defaults.authProvider,
    bundleFingerprint: normalizeStringOverride(overrides.bundleFingerprint, defaults.bundleFingerprint),
    environmentName: normalizeStringOverride(overrides.environmentName, defaults.environmentName),
    release: normalizeStringOverride(overrides.release, defaults.release ?? "dev-local"),
    adminRoles: normalizeAdminRolesOverride(overrides.adminRoles, defaults.adminRoles),
    firebaseConfig: isObject(overrides.firebaseConfig) ? (overrides.firebaseConfig as AdminDashboardFirebaseConfig) : defaults.firebaseConfig,
    observability: isObject(overrides.observability)
      ? ({
          cloudProjectId: normalizeStringOverride(
            overrides.observability.cloudProjectId,
            defaults.observability?.cloudProjectId ?? "",
          ) || undefined,
          cloudRunServiceName: normalizeStringOverride(
            overrides.observability.cloudRunServiceName,
            defaults.observability?.cloudRunServiceName ?? "",
          ) || undefined,
        } satisfies AdminDashboardObservabilityConfig)
      : defaults.observability,
  };
}

export function resolveAdminDashboardRuntimeConfig(
  overrides?: Partial<AdminDashboardRuntimeConfig>,
): AdminDashboardRuntimeConfig {
  const defaults = createRuntimeConfigFromEnv(readEnv());
  const normalizedOverrides = normalizeRuntimeConfigOverrides(overrides, defaults);
  return {
    ...defaults,
    ...normalizedOverrides,
    adminRoles: normalizedOverrides.adminRoles ?? defaults.adminRoles,
    firebaseConfig: normalizedOverrides.firebaseConfig ?? defaults.firebaseConfig,
  };
}

export function loadAdminDashboardRuntimeConfig(): AdminDashboardRuntimeConfig {
  return resolveAdminDashboardRuntimeConfig();
}
